import { ITask, TaskStatus } from '@common/interfaces/task'
import { ITaskProjectTask } from '@common/interfaces/taskProject'

export interface IPlacementMove {
  taskId: string
  sectionId: string | null
  position?: number
}

// How a surface stores a row's placement: My Tasks and project boards keep it in different fields.
export interface IPlacementAccess<T> {
  sectionOf: (row: T) => string | null
  positionOf: (row: T) => number
  place: (row: T, sectionId: string | null, position: number | null) => T
}

export const MY_TASKS_PLACEMENT: IPlacementAccess<ITask> = {
  sectionOf: (task) => task.myTasksSectionId ?? null,
  positionOf: (task) => task.myTasksPosition ?? 0,
  place: (task, myTasksSectionId, myTasksPosition) => ({
    ...task,
    myTasksSectionId,
    myTasksPosition,
  }),
}

export const PROJECT_PLACEMENT: IPlacementAccess<ITaskProjectTask> = {
  sectionOf: (task) => task.placement.sectionId,
  positionOf: (task) => task.placement.position,
  place: (task, sectionId, position) => ({
    ...task,
    placement: {
      ...task.placement,
      sectionId: sectionId ?? task.placement.sectionId,
      position: position ?? task.placement.position,
    },
  }),
}

/**
 * Applies a move the way the server's repositionPlacement does: close the gap in the source
 * section, open the slot in the target (or append), then write the row. The moved row goes to
 * the end of the array; callers order sections as their list does. Returns `rows` itself when
 * the task isn't in it.
 */
export const repositionRows = <T extends { id: string }>(
  rows: T[],
  move: IPlacementMove,
  access: IPlacementAccess<T>,
): T[] => {
  const moved = rows.find((row) => row.id === move.taskId)
  if (!moved) {
    return rows
  }
  const { sectionOf, positionOf, place } = access
  const fromSectionId = sectionOf(moved)
  const fromPosition = positionOf(moved)

  const rest = rows
    .filter((row) => row !== moved)
    .map((row) =>
      fromSectionId && sectionOf(row) === fromSectionId && positionOf(row) > fromPosition
        ? place(row, fromSectionId, positionOf(row) - 1)
        : row,
    )

  const toSectionId = move.sectionId
  if (!toSectionId) {
    return [...rest, place(moved, null, null)]
  }
  const targetPositions = rest.filter((row) => sectionOf(row) === toSectionId).map(positionOf)
  const toPosition = move.position ?? Math.max(-1, ...targetPositions) + 1
  const shifted = rest.map((row) =>
    sectionOf(row) === toSectionId && positionOf(row) >= toPosition
      ? place(row, toSectionId, positionOf(row) + 1)
      : row,
  )
  return [...shifted, place(moved, toSectionId, toPosition)]
}

/**
 * The single-row moves that land `taskIds`, in order, as a block directly above `target` — where
 * the drop line is drawn. The server applies moves one at a time, so each position is measured
 * against the rows as the moves before it leave them, minus the moving row itself.
 */
export const planMovesAboveRow = <T extends { id: string }>(
  rows: T[],
  taskIds: string[],
  target: T,
  access: IPlacementAccess<T>,
): IPlacementMove[] => {
  const { sectionOf, positionOf } = access
  const sectionId = sectionOf(target)
  let current = rows
  return taskIds.map((taskId) => {
    const currentTarget = current.find((row) => row.id === target.id) ?? target
    const mover = current.find((row) => row.id === taskId)
    const isAboveTargetInSection =
      !!mover && sectionOf(mover) === sectionId && positionOf(mover) < positionOf(currentTarget)
    const move = {
      taskId,
      sectionId,
      position: positionOf(currentTarget) - Number(isAboveTargetInSection),
    }
    current = repositionRows(current, move, access)
    return move
  })
}

/**
 * Sections in the order a ReorderSections call writes. Sections the ids don't name (a board's
 * pinned default) keep their place ahead of the reordered ones.
 */
export const reorderSectionsByIds = <T extends { id: string; position: number }>(
  sections: T[],
  sectionIds: string[],
): T[] => {
  const byId = new Map(sections.map((section) => [section.id, section]))
  const reordered = sectionIds.flatMap((id): T[] => {
    const section = byId.get(id)
    return section ? [section] : []
  })
  const pinned = sections.filter((section) => !sectionIds.includes(section.id))
  return [...pinned, ...reordered].map((section, position) => ({ ...section, position }))
}

/**
 * A MoveSubtask call as the reducers preview it. `parentTaskId` absent keeps the current parent,
 * null promotes to top level. `sectionId`/`position` say where a promoted task lands; each surface
 * reads them as its own placement.
 */
export interface ISubtaskMove {
  taskId: string
  parentTaskId?: string | null
  position?: number
  sectionId?: string | null
}

// Mirrors INCOMPLETE_SUBTASK_COUNT_SUBQUERY in ListMyTasks.
const isOpen = (task: ITask): boolean =>
  task.status !== TaskStatus.Complete && task.status !== TaskStatus.Archived

const withSubtasks = <T extends ITask>(
  parent: T,
  subtasks: ITask[] | undefined,
  delta: number,
  moved: ITask,
): T => ({
  ...parent,
  subtasks: subtasks?.map((subtask, index) => ({ ...subtask, subtaskPosition: index })),
  subtaskCount: (parent.subtaskCount ?? 0) + delta,
  incompleteSubtaskCount: (parent.incompleteSubtaskCount ?? 0) + delta * Number(isOpen(moved)),
})

/**
 * Applies a move the way MoveSubtask does: take the task out of its parent's list (or out of the
 * top-level rows), then insert it at `position` in the post-removal order, or append. A promoted
 * task comes back as `detached` for the caller to place as a row. Returns `rows` itself when the
 * task isn't loaded, or when the server would refuse the move (a task with its own subtasks can't
 * be nested), so the refetch is left to show what happened.
 */
export const applySubtaskMove = <T extends ITask>(
  rows: T[],
  move: ISubtaskMove,
): { rows: T[]; detached: ITask | null } => {
  const unchanged: { rows: T[]; detached: ITask | null } = { rows, detached: null }
  const oldParent = rows.find((row) => row.subtasks?.some((subtask) => subtask.id === move.taskId))
  const topLevel = rows.find((row) => row.id === move.taskId)
  const moving = oldParent?.subtasks?.find((subtask) => subtask.id === move.taskId) ?? topLevel
  const targetParentId =
    move.parentTaskId === undefined ? (oldParent?.id ?? null) : move.parentTaskId
  if (!moving || (!oldParent && !targetParentId) || (targetParentId && moving.subtaskCount)) {
    return unchanged
  }

  let next = rows
    .filter((row) => row !== topLevel)
    .map((row) =>
      row === oldParent
        ? withSubtasks(
            row,
            row.subtasks?.filter((subtask) => subtask !== moving),
            -1,
            moving,
          )
        : row,
    )

  if (!targetParentId) {
    return { rows: next, detached: { ...moving, parentTaskId: null, subtaskPosition: null } }
  }

  const placed = { ...moving, parentTaskId: targetParentId }
  next = next.map((row) => {
    if (row.id !== targetParentId) {
      return row
    }
    const siblings = row.subtasks ? [...row.subtasks] : undefined
    siblings?.splice(move.position ?? siblings.length, 0, placed)
    return withSubtasks(row, siblings, 1, moving)
  })
  return { rows: next, detached: null }
}
